import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getApiErrorMessage } from 'lib/api';
import { usePinterestAdGroups } from '../api';
import type { PinterestCampaignWithAdGroups } from '../api';
import type { PinterestAdGroupRow, PinterestGridRow } from '../types';
import { isPinterestAdGroupRow } from '../types';

const EMPTY: string[] = [];

const usePinterestAdGroupsData = () => {
  const { setValue } = useFormContext();
  const accountId = (useWatch({ name: 'accountId' }) as string | undefined) ?? '';
  const selectedCampaigns = (useWatch({ name: 'selectedCampaigns' }) as string[] | undefined) ?? EMPTY;
  const selectedAdGroups = (useWatch({ name: 'selectedAdGroups' }) as string[] | undefined) ?? EMPTY;
  const filterByName = (useWatch({ name: 'filterAdGroupsByName' }) as string[] | undefined) ?? EMPTY;

  const { data, isLoading, isError, error } = usePinterestAdGroups({
    params: { accountId, campaignIds: selectedCampaigns },
  });

  const adGroupsData = useMemo<PinterestCampaignWithAdGroups[]>(() => data ?? [], [data]);

  useEffect(() => {
    if (data) {
      setValue('$adGroupsData', data, { shouldValidate: true });
    }
  }, [data, setValue]);

  const allRows = useMemo<PinterestGridRow[]>(
    () =>
      adGroupsData.flatMap((campaign) => [
        campaign,
        ...campaign.ad_groups.map<PinterestAdGroupRow>((adGroup) => ({
          ...adGroup,
          campaign_id: campaign.campaign_id,
          campaign_name: campaign.campaign_name,
          account_id: campaign.account_id,
          isAdGroup: true,
        })),
      ]),
    [adGroupsData],
  );

  const gridRows = useMemo<PinterestGridRow[]>(() => {
    if (!filterByName.length) return allRows;

    const selected = new Set(selectedAdGroups);
    return allRows.filter((row) => {
      if (!isPinterestAdGroupRow(row)) return true;
      if (selected.has(row.ad_group_id)) return true;

      const name = row.ad_group_name.toLowerCase();
      return filterByName.every((chip) => name.includes(chip.toLowerCase()));
    });
  }, [allRows, filterByName, selectedAdGroups]);

  const selectedAdGroupRows = useMemo(() => {
    const selected = new Set(selectedAdGroups);
    return allRows.filter(
      (row): row is PinterestAdGroupRow => isPinterestAdGroupRow(row) && selected.has(row.ad_group_id),
    );
  }, [allRows, selectedAdGroups]);

  const selectedFlows = useMemo(
    () =>
      [...new Set(selectedAdGroupRows.map((row) => row.flow).filter((flow): flow is string => !!flow))].sort(),
    [selectedAdGroupRows],
  );

  return {
    adGroupsData,
    gridRows,
    selectedAdGroups,
    selectedAdGroupRows,
    selectedFlows,
    isLoading,
    isError,
    errorMessage: isError ? getApiErrorMessage(error, 'Failed to load ad groups') : undefined,
  };
};

export default usePinterestAdGroupsData;
